import { ModalView } from '@/components/MetaModal.vue'
import { SelectableStoreDispatchers, UniqKey } from '@/models/generics'
import { CtStorageLocation, CtTask, LocationBufferItem } from '@/models/interfaces/content-type'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'

export type SelectedBufferEntry = LocationBufferItem & {
  id: UUID
}

export type BufferItemListEntry = {
  [index: UUID]: { [index: UUID]: LocationBufferItem }
  sys: { id: UUID }
}

export type LocationLabelValuePair = {
  label: string,
  value: string
  catalogue?: UUID
}

export type LocationStockItemData = {
  stockItemId: UUID,
  locationId: UUID,
  catalogue?: UUID,
  count: number,
  selected: number
}

export type Meta = {
  storage_location: UUID
  items: Record<UUID, any>
}

export const hideHtmlElements = (classList: string) =>
  document.querySelectorAll<HTMLElement>(classList).forEach((element) => {
    if (!element) throw new Error('hideElement | element not found')
    element.style.display = 'none'
  })

export const showHtmlElements = (classList: string) =>
  document.querySelectorAll<HTMLElement>(classList).forEach((element) => {
    if (!element) throw new Error('showElement | element not found')
    element.style.display = ''
  })

export const getBufferItemById = (id: UUID, activeBuffer: BufferItemListEntry[]) => {
  const item = activeBuffer.find((bufferItem) => bufferItem[id])
  if (!item) throw new Error('selectBufferItem | buffer item undefinend')
  const total = Object.entries(item[id]).reduce(
    (acc, [key, value]) => acc + (value.count - value.transiting),
    0
  )
  return { item, show: true, total }
}

export const getEmptyLocationsAsLabelValuePair = async ($directr: any, storage_location: any): Promise<LocationLabelValuePair[]> => {
  try {
    const locationArray = await $directr.get('inventory_location', [{field: 'count', method: 'lt', match: 1}, {field: 'stock_item', method: 'ne'}, {field: 'storage_location', method: 'match', match: storage_location.sys.id}])
    return locationArray.items.map(location => ({label: location.id, value: location.sys.id, catalogue: location.catalogue}))
  } catch (e) {
    throw new Error('getEmptyLocationsAsLabelValuePair | could not get inventory locations')
  }
}

export const updateBufferStockItem = async (storageLocationId: UUID, stockItemId: UUID, count: number, batchId: string | Array<string>, $directr: any): Promise<void> => {
  const storageLocation = await getStorageLocationById(storageLocationId, $directr)
  if (!storageLocation.buffer) throw new Error('updateBufferStockItem | buffer was undefined')

  if (!Array.isArray(batchId)) throw new Error('batchId was not an array | unable to process')
  batchId.reduce((acc, batch) => {
    const batchData =  storageLocation.buffer?.[stockItemId].find(item => item.batch === batch)
    if (batchData) {
        const updateBufferItem: LocationBufferItem & { id: UUID} = {
          ...batchData,
          count: Math.max(0, batchData.count - acc),
          transiting: Math.max(0, batchData.transiting - acc),
          id: stockItemId
        }

        try {
          $directr.put('warehouse.buffer', storageLocation.sys.id, updateBufferItem)
        } catch (e) {
          throw new Error('updateBufferStockItem | could not update buffer')
        }
        acc= Math.max(0,acc - batchData.count)
    }
    return acc
  }, count)

}

export const updateTaskStockItem = (activeTask: CtTask, stockItemId: UUID, count: number): CtTask => {
  const updatedTask = activeTask
  if (!updatedTask.meta) throw new Error ('updateTaskStockItem | activeTask was undefined')

  // prevent a count larger than the remaining quantity
  const c = (count > updatedTask.meta.items[stockItemId].quantity) ? updatedTask.meta.items[stockItemId].quantity : count

  updatedTask.meta.items[stockItemId].resolved += c
  updatedTask.meta.items[stockItemId].quantity -= c
  return updatedTask
}

export const updateInventoryLocation = async (data, $directr: any) => {
  try {
    const payload = { stock_item: data.stockItemId, location: data.locationId, count: data.count, catalogue: data.catalogue }
    await $directr.put('inventory.add', { ...payload, catalogue: ''})
  } catch (e) {
    throw new Error('updateInventoryLocation | failed to update/add inventory location')
  }
}

export const getStorageLocationById = async (id: UUID, $directr: any): Promise<CtStorageLocation>  => {
  try {
    const { items: result} = await $directr.get('storage_location', id)
    return result[0]
  } catch (e) {
    throw new Error('getStorageLocationById | could not get storage location')
  }
}

export const getTasksForCurrentStorageLocation = (tasks: Array<CtTask>, storageLocationId: UUID): Array<CtTask> => {
  const filteredTasks = tasks.filter(task => {
    if (!task.meta) throw new Error(`getTaskForCurrentStorageLocation | Task ${task.id} has no meta`)
    return task.meta.storage_location === storageLocationId
  })
  return filteredTasks
}

export const getLocationsFromTasks = async (tasks: Array<CtTask>, $directr: any): Promise<any> => {
  const allLocations = {}
  const stockItemIds: (Record<string, any> | UniqKey)[] = []
  try {
    const stockItemIds = tasks.map(task => {
      return task.items
    }).flat()
  } catch (e) {
    throw new Error('getLocationsFromTask | could not get stock item ids')
  }

  for (let i = 0; i < stockItemIds.length; i++) {
    const { items: locations } = await $directr.get('inventory_location', [{ field: 'stock_item', method: 'match', match: stockItemIds[i] }, {field: 'storage_location', method: 'match', match: tasks[0].meta?.storage_location}, { field: 'catalogues', method: 'ne' }], { limit: 3 })
    if (!locations) throw new Error('getLocationsFromTask | could not get locations')
    allLocations[stockItemIds[i] as UUID] = locations
  }
  return allLocations
}
